import { useState, useEffect, type FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import type { PriceListItem } from '../../types';

export default function PriceListPage() {
  const [priceList, setPriceList] = useState<PriceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const [form, setForm] = useState({
    category: 'Castors',
    item_code: '',
    item_name: '',
    size: '',
    price: '',
    effective_date: new Date().toISOString().split('T')[0],
  });

  const categories = [
    { id: 'all', label: 'All Categories' },
    { id: 'Castors', label: 'Castors' },
    { id: 'Wheels', label: 'Wheels' },
    { id: 'BB', label: 'Ball Bearings' },
    { id: 'GB', label: 'Bushes' },
    { id: 'TG', label: 'Thread Guards' },
    { id: 'NB', label: 'Nut Bolts' },
  ];

  useEffect(() => {
    fetchPriceList();
  }, []);

  async function fetchPriceList() {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('price_list')
        .select('*')
        .order('category', { ascending: true })
        .order('item_code', { ascending: true });

      if (error) {
        setError(error.message);
        return;
      }

      setPriceList(data || []);
    } catch (err) {
      setError('Failed to fetch price list');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setForm({
      category: 'Castors',
      item_code: '',
      item_name: '',
      size: '',
      price: '',
      effective_date: new Date().toISOString().split('T')[0],
    });
    setEditingId(null);
    setShowForm(false);
  }

  function editPrice(price: PriceListItem) {
    const rawCode =
      typeof price.item_code === 'object' && price.item_code !== null
        ? (price.item_code as any).result || (price.item_code as any).code || JSON.stringify(price.item_code)
        : String(price.item_code || '');

    setForm({
      category: price.category,
      item_code: rawCode,
      item_name: price.item_name || '',
      size: price.size || '',
      price: price.price.toString(),
      effective_date: price.effective_date || '',
    });
    setEditingId(price.id || null);
    setShowForm(true);
  }

  async function deletePrice(id: number) {
    if (!window.confirm('Are you sure you want to delete this price item?')) return;
    try {
      const { error } = await supabase.from('price_list').delete().eq('id', id);
      if (error) throw error;
      setPriceList(priceList.filter((p) => p.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete price');
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.category || !form.item_code || !form.price) return;

    try {
      const priceData = {
        category: form.category,
        item_code: form.item_code,
        item_name: form.item_name || null,
        size: form.size || null,
        price: parseFloat(form.price),
        effective_date: form.effective_date || null,
      };

      if (editingId) {
        const { error } = await supabase.from('price_list').update(priceData).eq('id', editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('price_list').insert([priceData]);
        if (error) throw error;
      }

      resetForm();
      fetchPriceList();
    } catch (err: any) {
      setError(err.message || 'Failed to save price item');
    }
  }

  const filteredPriceList = priceList.filter((p) => {
    const codeStr =
      typeof p.item_code === 'object' && p.item_code !== null
        ? (p.item_code as any).result || (p.item_code as any).code || ''
        : String(p.item_code || '');

    const matchesCat =
      selectedCategory === 'all' ||
      p.category?.toLowerCase() === selectedCategory.toLowerCase();

    const q = searchQuery.toLowerCase();
    const matchesSearch =
      searchQuery === '' ||
      p.category.toLowerCase().includes(q) ||
      codeStr.toLowerCase().includes(q) ||
      (p.item_name && p.item_name.toLowerCase().includes(q)) ||
      (p.size && p.size.toLowerCase().includes(q));

    return matchesCat && matchesSearch;
  });

  const getCategoryBadgeClass = (category: string) => {
    switch (category?.toLowerCase()) {
      case 'castors':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'wheels':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'bb':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'gb':
      case 'bush':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'tg':
        return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      case 'nb':
        return 'bg-slate-100 text-slate-700 border-slate-200';
      default:
        return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-slate-900">Component Price List</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Master rate matrices for 13 assembly components ({priceList.length} verified items)
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 hover:bg-blue-700 active:scale-95 text-white text-xs sm:text-sm font-bold px-4 py-2.5 rounded-xl shadow-md shadow-blue-600/20 transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap"
        >
          <span>{showForm ? '✕' : '+'}</span>
          <span>{showForm ? 'Close Form' : 'Add Price Entry'}</span>
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs">
          {error}
        </div>
      )}

      {/* Add / Edit Form Card */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-elevated border border-slate-200/80 p-6 sm:p-7 space-y-4">
          <div className="flex justify-between items-center border-b border-slate-100 pb-3">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600">
                {editingId ? 'Modify Rate' : 'New Component'}
              </span>
              <h2 className="text-base font-bold text-slate-900">
                {editingId ? 'Edit Price Entry' : 'Create New Price Entry'}
              </h2>
            </div>
            <button
              onClick={resetForm}
              className="text-xs text-slate-400 hover:text-slate-600 font-bold p-1 rounded hover:bg-slate-100"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 text-xs">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block font-bold text-slate-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 bg-white"
                  required
                >
                  <option value="Castors">Castors (Bracket)</option>
                  <option value="Wheels">Wheels</option>
                  <option value="BB">Ball Bearings</option>
                  <option value="GB">Bushes (GB)</option>
                  <option value="TG">Thread Guards (TG)</option>
                  <option value="NB">Nut Bolts (NB)</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">
                  Item Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. MSFB/SW/4 x 1 1/4"
                  value={form.item_code}
                  onChange={(e) => setForm({ ...form, item_code: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 font-mono"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Item Name</label>
                <input
                  type="text"
                  placeholder="e.g. MSFB Swivel"
                  value={form.item_name}
                  onChange={(e) => setForm({ ...form, item_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Size / Dimension</label>
                <input
                  type="text"
                  placeholder="e.g. 4 x 1 1/4"
                  value={form.size}
                  onChange={(e) => setForm({ ...form, size: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 font-mono"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">
                  Rate / Unit Price (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  placeholder="310.00"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 font-mono font-bold"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Effective Date</label>
                <input
                  type="date"
                  value={form.effective_date}
                  onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-sm transition-colors cursor-pointer"
              >
                {editingId ? 'Update Price' : 'Save Price Entry'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Category Filter Pills */}
      <div className="flex flex-wrap gap-2 pb-1">
        {categories.map((cat) => {
          const count =
            cat.id === 'all'
              ? priceList.length
              : priceList.filter((p) => p.category?.toLowerCase() === cat.id.toLowerCase()).length;

          return (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer ${
                selectedCategory === cat.id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              <span>{cat.label}</span>
              <span
                className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                  selectedCategory === cat.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Table Container Card */}
      <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-slate-200/80 bg-slate-50/60 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="w-full sm:w-96 relative">
            <input
              type="text"
              placeholder="Search by item code, description, category, size..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-xs sm:text-sm bg-white border border-slate-300 rounded-xl focus:border-blue-500 shadow-sm text-slate-900 placeholder-slate-400 transition-colors"
            />
            <span className="absolute left-3.5 top-3 text-slate-400 text-sm">🔍</span>
          </div>

          <div className="text-xs font-medium text-slate-500">
            Showing <strong className="text-slate-900">{filteredPriceList.length}</strong> rate records
          </div>
        </div>

        {loading ? (
          <div className="p-16 text-center text-slate-500 space-y-3">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto"></div>
            <span className="text-xs font-semibold">Loading price list...</span>
          </div>
        ) : filteredPriceList.length === 0 ? (
          <div className="p-16 text-center text-slate-400 space-y-2">
            <span className="text-3xl">🏷️</span>
            <p className="text-sm font-bold text-slate-700">No price entries found</p>
            <p className="text-xs text-slate-500">Try selecting another category or clear your search term.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs divide-y divide-slate-200">
              <thead className="bg-slate-50/80 text-slate-500 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="px-5 py-3.5">Category</th>
                  <th className="px-4 py-3.5">Item Code</th>
                  <th className="px-4 py-3.5">Item Name / Description</th>
                  <th className="px-4 py-3.5">Size</th>
                  <th className="px-4 py-3.5 text-right">Unit Rate (₹)</th>
                  <th className="px-4 py-3.5">Effective Date</th>
                  <th className="px-5 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredPriceList.map((p) => {
                  const codeStr =
                    typeof p.item_code === 'object' && p.item_code !== null
                      ? (p.item_code as any).result || (p.item_code as any).code || JSON.stringify(p.item_code)
                      : String(p.item_code || '');

                  return (
                    <tr key={p.id} className="hover:bg-slate-50/70 transition-colors">
                      <td className="px-5 py-3.5">
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border ${getCategoryBadgeClass(
                            p.category
                          )}`}
                        >
                          {p.category}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 font-mono font-bold text-slate-900 text-xs">{codeStr}</td>
                      <td className="px-4 py-3.5 text-slate-700 font-medium">{p.item_name || <span className="text-slate-300">-</span>}</td>
                      <td className="px-4 py-3.5 font-mono text-slate-600">{p.size || <span className="text-slate-300">-</span>}</td>
                      <td className="px-4 py-3.5 text-right font-extrabold text-slate-900 text-xs">
                        ₹{Number(p.price || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3.5 text-slate-500 font-medium">{p.effective_date || <span className="text-slate-300">-</span>}</td>
                      <td className="px-5 py-3.5 text-right whitespace-nowrap space-x-1.5">
                        <button
                          onClick={() => editPrice(p)}
                          className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold rounded-lg text-xs transition-colors"
                        >
                          Edit ✏️
                        </button>
                        <button
                          onClick={() => p.id && deletePrice(p.id)}
                          className="px-2.5 py-1 bg-red-50 hover:bg-red-100 text-red-700 font-bold rounded-lg text-xs transition-colors"
                        >
                          Delete 🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
